/**
 * Canvas-based real-time waveform visualisation.
 *
 * Draws frequency bars from an AnalyserNode using requestAnimationFrame.
 * Matches the HUD glass aesthetic with configurable colour.
 */

import { useEffect, useRef } from "react";

interface WaveformProps {
  analyser: AnalyserNode | null;
  color?: string; // CSS colour, e.g. "0, 255, 200"
  active?: boolean;
  width?: number;
  height?: number;
}

export function Waveform({
  analyser,
  color = "0, 255, 200",
  active = true,
  width = 200,
  height = 60,
}: WaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyser || !active) {
      // Clear canvas when inactive
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const barCount = Math.min(bufferLength, 32);
    const barWidth = width / barCount - 1;

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < barCount; i++) {
        // Sample evenly across the frequency bins
        const idx = Math.floor((i / barCount) * bufferLength);
        const value = dataArray[idx] / 255;
        const barHeight = Math.max(2, value * height);
        const y = (height - barHeight) / 2;
        const alpha = 0.3 + value * 0.7;

        ctx.fillStyle = `rgba(${color}, ${alpha})`;
        ctx.beginPath();
        ctx.roundRect(
          i * (barWidth + 1),
          y,
          barWidth,
          barHeight,
          2,
        );
        ctx.fill();
      }
    };

    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [analyser, active, color, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="opacity-90"
      style={{ width, height }}
    />
  );
}
